import { Fragment, useState } from "react";
import PropTypes from "prop-types";
import ExpandableRow from "./ExpandableRow";
import CategorizedListRoot from "../CategorizedListRoot";
import NumberOfChanges from "../../00-atoms/NumberOfChanges";
import makeStyles from "@mui/styles/makeStyles";
import UndoIcon from "@mui/icons-material/Undo";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { compose, filter, not, pathEq, prop } from "ramda";
import { Typography } from "@mui/material";
import SimpleButton from "@/components/00-atoms/SimpleButton/index.jsx";

const useStyles = makeStyles(() => ({
  noPadding: {
    padding: 0
  }
}));

const defaultProps = {
  onSelectAll: () => {},
  onDeselectAll: () => {},
  selectAll: false,
  categories: [],
  changes: [],
  disableReverting: false,
  hideAmountOfChanges: false,
  isExpanded: false,
  messages: {
    undo: "Undo",
    changesText: "Muutokset:"
  },
  showCategoryTitles: false
};

const ExpandableRowRoot = ({
  onSelectAll = defaultProps.onSelectAll(),
  onDeselectAll = defaultProps.onDeselectAll(),
  selectAll = defaultProps.selectAll,
  selectAllText = "",
  anchor,
  categories = defaultProps.categories,
  changes = defaultProps.changes,
  children,
  code,
  disableReverting = defaultProps.disableReverting,
  hideAmountOfChanges = defaultProps.hideAmountOfChanges,
  index,
  isExpanded = defaultProps.isExpanded,
  messages = defaultProps.messages,
  onChangesRemove,
  onUpdate,
  sectionId,
  showCategoryTitles = defaultProps.showCategoryTitles,
  title,
  isReadOnly,
  showValidationErrors
}) => {
  const classes = useStyles();
  const [isToggledOpen, setIsToggleOpen] = useState(false);

  const onToggle = (...props) => {
    setIsToggleOpen(prop(1, props));
  };

  const amountOfRelevantChanges = filter(
    compose(not, pathEq(["properties", "metadata", "isOnlyForUI"], true)),
    changes
  );

  const [isChecked, setIsChecked] = useState(false);

  return (
    <Fragment>
      {categories && (
        <ExpandableRow
          shouldBeExpanded={isExpanded}
          onToggle={onToggle}
          isToggledOpen={isToggledOpen}
          id={anchor}>
          <Typography
            component="h4"
            variant="h4"
            classes={{ root: classes.noPadding }}
            data-slot="title">
            {code && <span className="pr-6">{code}</span>}
            <span>{title}</span>
          </Typography>
          <div data-slot="info">
            {amountOfRelevantChanges.length > 0 && (
              <div className="flex items-center">
                {!hideAmountOfChanges && (
                  <NumberOfChanges
                    changes={amountOfRelevantChanges}
                    id={anchor}
                    messages={messages}
                  />
                )}
                {!disableReverting && (
                  <span className="mx-6">
                    <Tooltip title={messages.undo}>
                      <Button
                        component="span"
                        color="primary"
                        className={classes.noPadding}
                        endIcon={<UndoIcon />}
                        onClick={e => {
                          e.stopPropagation();
                          return onChangesRemove(sectionId, anchor, index);
                        }}
                        size="small">
                        {messages.undo}
                      </Button>
                    </Tooltip>
                  </span>
                )}
              </div>
            )}
          </div>
          <div
            data-slot="content"
            className={`w-full ${!children ? "p-8" : ""}`}>
            {selectAll && (
              <div
                className={
                  "flex flex-wrap flex-col justify-between items-center sm:flex-row"
                }
                style={{
                  paddingRight: "2rem",
                  paddingLeft: "4.5rem",
                  height: "0"
                }}>
                <div className={"flex-2"}>
                  <div className={"flex-2"}>
                    <div className={"flex"}>
                      <div className={"flex"}>
                        <div className={"flex-1"}></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={"flex-1"} style={{ paddingTop: "2rem" }}>
                  <SimpleButton
                    text={selectAllText}
                    style={{ width: "100%" }}
                    onClick={() => {
                      onSelectAll({ anchor });
                      setIsChecked(!isChecked);
                      if (isChecked) {
                        onDeselectAll({ anchor });
                      }
                    }}></SimpleButton>
                </div>
              </div>
            )}
            {!children && (isExpanded || isToggledOpen) ? (
              <CategorizedListRoot
                anchor={anchor}
                categories={categories}
                changes={changes}
                index={index}
                onUpdate={onUpdate}
                sectionId={sectionId}
                showCategoryTitles={showCategoryTitles}
                isReadOnly={isReadOnly}
                showValidationErrors={showValidationErrors}
              />
            ) : (
              children
            )}
          </div>
        </ExpandableRow>
      )}
    </Fragment>
  );
};

ExpandableRowRoot.propTypes = {
  anchor: PropTypes.string,
  categories: PropTypes.array,
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  changes: PropTypes.array,
  code: PropTypes.string,
  disableReverting: PropTypes.bool,
  hideAmountOfChanges: PropTypes.bool,
  index: PropTypes.number,
  isExpanded: PropTypes.bool,
  messages: PropTypes.object,
  onChangesRemove: PropTypes.func,
  onUpdate: PropTypes.func,
  sectionId: PropTypes.string,
  showCategoryTitles: PropTypes.bool,
  title: PropTypes.string,
  isReadOnly: PropTypes.bool,
  showValidationErrors: PropTypes.bool
};

export default ExpandableRowRoot;
